/**
 * lh-cart-shipping.js
 * Lolahome — Calculadora de Frete + régua de frete grátis (tema Horizon)
 *
 * Fluxo:
 *  1. Usuário digita o CEP (formatado 00000-000 automaticamente)
 *  2. Clica em "Calcular" (ou Enter) -> busca o estado via ViaCEP
 *  3. Busca fretes disponíveis via /cart/shipping_rates.json
 *  4. Exibe a opção mais barata e a mais rápida
 *  5. Atualiza a régua de frete grátis (#lh-free-shipping-bar)
 *
 * Configuração injetada via window.lhShippingConfig (snippets/lh-cart-shipping.liquid).
 */

import { StandardEvents } from '@shopify/events';

const STORAGE_KEY = 'lh_shipping_estimate';

function getConfig() {
  return window.lhShippingConfig || { globalThreshold: 0, stateRules: '', showBarWithoutCep: true };
}

// ─── Thresholds de frete grátis por estado ───────────────────────────────────────────────────────
// Formato da setting: "150 -> SP,RJ\n200 -> MG,ES"
function buildStateMap(raw) {
  const map = {};
  if (!raw) return map;
  raw.split('\n').forEach((line) => {
    const parts = line.split('->');
    if (parts.length !== 2) return;
    const amount = parseFloat(parts[0].trim().replace(',', '.'));
    if (Number.isNaN(amount)) return;
    parts[1]
      .split(',')
      .map((s) => s.trim().toUpperCase())
      .forEach((uf) => {
        if (uf) map[uf] = amount;
      });
  });
  return map;
}

function getThreshold(uf) {
  const config = getConfig();
  const map = buildStateMap(config.stateRules);
  return (uf && map[uf]) || config.globalThreshold || 0;
}

// ─── Cache (localStorage) ──────────────────────────────────────
function loadCache() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || null;
  } catch {
    return null;
  }
}
function saveCache(data) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}
function clearCache() {
  localStorage.removeItem(STORAGE_KEY);
}

// ─── Formatação de CEP ───────────────────────────────────────────────
function formatZip(value) {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  return digits.length > 5 ? digits.slice(0, 5) + '-' + digits.slice(5) : digits;
}
function rawZip(value) {
  return value.replace(/\D/g, '');
}

function formatMoney(amount) {
  return 'R$ ' + amount.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// ─── DOM helpers ──────────────────────────────────────────────────────────────────────────────────────
function getInput() {
  return document.getElementById('lh-shipping-zip');
}
function getBtn() {
  return document.getElementById('lh-shipping-btn');
}
function getErrorEl() {
  return document.getElementById('lh-shipping-error');
}
function getResultEl() {
  return document.getElementById('lh-shipping-result');
}
function getBar() {
  return document.getElementById('lh-free-shipping-bar');
}

function showError(msg) {
  const el = getErrorEl();
  if (!el) return;
  el.textContent = msg;
  el.style.display = msg ? '' : 'none';
  if (msg) {
    const result = getResultEl();
    if (result) result.innerHTML = '';
  }
}

function showResult(html) {
  const el = getResultEl();
  if (!el) return;
  el.innerHTML = html;
  const err = getErrorEl();
  if (err) {
    err.textContent = '';
    err.style.display = 'none';
  }
}

function setLoading(loading) {
  const btn = getBtn();
  if (!btn) return;
  btn.disabled = loading;
  btn.textContent = loading ? 'Calculando...' : 'Calcular';
}

// ─── Régua de frete grátis ───────────────────────────────────────────────────────────────
function renderBar(threshold, cartTotal) {
  const bar = getBar();
  if (!bar) return;
  if (!threshold) {
    bar.style.display = 'none';
    return;
  }

  const remaining = Math.max(0, threshold - cartTotal);
  const percentage = Math.min(100, Math.round((cartTotal / threshold) * 100));

  const textEl = bar.querySelector('.lh-free-shipping-bar__text');
  if (textEl) {
    textEl.textContent =
      remaining <= 0 ? 'Você ganhou frete grátis!' : 'Faltam ' + formatMoney(remaining) + ' para frete grátis';
  }

  const fillEl = bar.querySelector('.lh-free-shipping-bar__fill');
  if (fillEl) fillEl.style.width = percentage + '%';

  bar.style.display = '';
}

function showDefaultBar(cartTotal) {
  const config = getConfig();
  const bar = getBar();
  if (!bar) return;

  if (!config.showBarWithoutCep) {
    bar.style.display = 'none';
    return;
  }

  if (config.globalThreshold) {
    renderBar(config.globalThreshold, cartTotal);
    return;
  }

  const textEl = bar.querySelector('.lh-free-shipping-bar__text');
  const fillEl = bar.querySelector('.lh-free-shipping-bar__fill');
  if (textEl) textEl.textContent = 'Calcule o frete para conferir o frete grátis!';
  if (fillEl) fillEl.style.width = '0%';
  bar.style.display = '';
}

// ─── Total atual do carrinho ───────────────────────────────────────────────
async function getCurrentCartTotal() {
  try {
    const resp = await fetch('/cart.js');
    const cart = await resp.json();
    return cart.total_price / 100;
  } catch {
    return 0;
  }
}

// ─── ViaCEP: busca estado pelo CEP ────────────────────────────────────────────────────
function fetchProvince(zip) {
  return fetch('https://viacep.com.br/ws/' + zip + '/json', { cache: 'force-cache' })
    .then((r) => r.json())
    .then((data) => {
      if (data.erro) throw new Error('CEP_NOT_FOUND');
      return data.uf;
    });
}

// ─── Shopify: busca fretes disponíveis ─────────────────────────────────────────────────────────
function fetchShippingRates(zip, province) {
  const url =
    '/cart/shipping_rates.json' +
    '?shipping_address%5Bzip%5D=' +
    zip +
    '&shipping_address%5Bcountry%5D=Brazil' +
    '&shipping_address%5Bprovince%5D=' +
    province;

  return fetch(url).then((r) => r.json());
}

function formatDays(rate) {
  const days = rate.delivery_days;
  if (!days) return '';
  if (Array.isArray(days)) {
    return days[0] === days[1] ? days[0] + ' dias úteis' : days[0] + '-' + days[1] + ' dias úteis';
  }
  return days + ' dias úteis';
}

function rateRow(rate) {
  const isFree = parseFloat(rate.price) === 0;
  let html =
    '<li class="lh-shipping__rate' +
    (isFree ? ' lh-shipping__rate--free' : '') +
    '">' +
    '<span class="lh-shipping__rate-name">' +
    rate.presentment_name +
    '</span>' +
    '<span class="lh-shipping__rate-price">' +
    (isFree ? 'Grátis' : formatMoney(parseFloat(rate.price))) +
    '</span>';
  if (formatDays(rate)) html += '<span class="lh-shipping__rate-days">em ' + formatDays(rate) + '</span>';
  html += '</li>';
  return html;
}

function buildResultHTML(rates) {
  if (!rates || rates.length === 0) {
    return '<p class="lh-shipping__result-none">Nenhuma opção de frete encontrada para este CEP.</p>';
  }

  const available = rates.filter((r) => {
    const name = (r.presentment_name || '').toLowerCase();
    return !name.includes('retire') && !name.includes('retirada');
  });

  if (available.length === 0) {
    return '<p class="lh-shipping__result-none">Nenhuma entrega disponível para este CEP.</p>';
  }

  const cheaper = available.reduce((a, b) => (parseFloat(a.price) <= parseFloat(b.price) ? a : b));
  const faster = available.reduce((a, b) => {
    const daysA = Array.isArray(a.delivery_days) ? a.delivery_days[1] : a.delivery_days || 99;
    const daysB = Array.isArray(b.delivery_days) ? b.delivery_days[1] : b.delivery_days || 99;
    return daysA <= daysB ? a : b;
  });
  const isSame = cheaper.name === faster.name;

  let html = '<ul class="lh-shipping__rate-list">' + rateRow(cheaper);
  if (!isSame) html += rateRow(faster);
  html += '</ul><p class="lh-shipping__checkout-hint">mais opções de frete no checkout</p>';

  return html;
}

// ─── Handler principal: clique em Calcular ────────────────────────────────────────────────────
async function handleCalculate() {
  const input = getInput();
  if (!input) return;

  const zip = rawZip(input.value);

  if (zip.length < 8) {
    showError('Informe um CEP válido (8 dígitos).');
    return;
  }

  setLoading(true);
  showError('');
  showResult('');

  try {
    const cartTotal = await getCurrentCartTotal();
    const cache = loadCache();

    let province;
    if (cache && cache.zip === zip) {
      province = cache.province;
    } else {
      province = await fetchProvince(zip);
    }

    const data = await fetchShippingRates(zip, province);
    showResult(buildResultHTML(data.shipping_rates));

    saveCache({ zip, province });
    renderBar(getThreshold(province), cartTotal);
  } catch (err) {
    if (err && err.message === 'CEP_NOT_FOUND') {
      showError('CEP não encontrado. Verifique e tente novamente.');
    } else {
      showError('Não foi possível calcular o frete. Tente novamente.');
      console.error('[lh-cart-shipping]', err);
    }
  } finally {
    setLoading(false);
  }
}

// ─── Restaura estado (após re-render do carrinho ou carregamento) ──────────────────────────────────────────────────────────
async function restoreState() {
  const cartTotal = await getCurrentCartTotal();

  if (cartTotal <= 0) {
    clearCache();
    const bar = getBar();
    if (bar) bar.style.display = 'none';
    return;
  }

  if (!getInput()) return;

  const cache = loadCache();
  if (!cache) {
    showDefaultBar(cartTotal);
    return;
  }

  renderBar(getThreshold(cache.province), cartTotal);

  const input = getInput();
  if (input && !input.value) input.value = formatZip(cache.zip);

  const resultEl = getResultEl();
  if (resultEl && !resultEl.innerHTML) {
    try {
      const data = await fetchShippingRates(cache.zip, cache.province);
      showResult(buildResultHTML(data.shipping_rates));
    } catch {
      // mantém a régua atualizada mesmo se o recálculo de fretes falhar
    }
  }
}

// ─── Event delegation (sobrevive a re-renders do carrinho) ───────────────────────────────────────────────
let listenersAttached = false;
function attachListeners() {
  if (listenersAttached) return;
  listenersAttached = true;

  document.addEventListener('input', (e) => {
    if (e.target.id !== 'lh-shipping-zip') return;
    e.target.value = formatZip(e.target.value);
  });

  document.addEventListener('keydown', (e) => {
    if (e.target.id !== 'lh-shipping-zip') return;
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCalculate();
    }
  });

  document.addEventListener('click', (e) => {
    if (e.target.id === 'lh-shipping-btn' || e.target.closest('#lh-shipping-btn')) {
      handleCalculate();
    }
  });

  document.addEventListener(StandardEvents.cartLinesUpdate, () => {
    setTimeout(restoreState, 150);
  });
}

attachListeners();
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', restoreState);
} else {
  restoreState();
}
